import { NgModule } from '@angular/core';
import { ExtraOptions, RouterModule, Routes } from '@angular/router';
import { GivebackPageComponent } from '@shared/giveback-page/giveback-page.component';
import { PrivacyPolicyPageComponent } from '@shared/privacy-policy-page/privacy-policy-page.component';
import { TermsOfUsePageComponent } from '@shared/terms-of-use-page/terms-of-use-page.component';
import { AboutUsPageComponent } from '@shared/about-us-page/about-us-page.component';
import { ContactUsPageComponent } from '@shared/contact-us-page/contact-us-page.component';
import { NewFaqPageComponent } from '@shared/new-faq-page/new-faq-page.component';
import { RedirectComponent } from './redirect.component';
import { NotSupportedComponent } from '@shared/not-supported/not-supported.component';
import { StaticPageComponent } from './static-page/static-page.component';
import {
  InsuranceProducerLicensureComponent
} from '@shared/insurance-producer-licensure/insurance-producer-licensure.component';
import { PmaUploadCoverageProofComponent } from './pma/pma-upload-coverage-proof/pma-upload-coverage-proof.component';
import { uploadGuard } from '@core/guards/upload.guard';
import { pmaModuleScopeGuard } from '@core/guards/pma-module-scope.guard';
import { kioskGuard } from '@core/guards/kiosk.guard';
import { PolicyTrackerComponent } from './components/policy-tracker/policy-tracker.component';
import { pointOfLeaseGuard } from '@core/guards/point-of-lease.guard';
import { assurantRedirectGuard } from '@core/guards/assurant.guard';
import { amtrustRedirectGuard } from './core/guards/amtrust.guard';

const routerOptions: ExtraOptions = {
  scrollPositionRestoration: 'enabled',
  anchorScrolling: 'enabled',
  scrollOffset: [0, 64]
};

const routes: Routes = [
  {
    path: '',
    component: RedirectComponent
  },
  {
    path: 'policy-tracker',
    component: PolicyTrackerComponent
  },
  // The legacy /residential quote flow was removed; stale links (old emails,
  // branding menu URLs, defaultStartingPage='residential') land on /renters.
  {
    path: 'residential/:token',
    redirectTo: '/renters'
  },
  {
    path: 'residential',
    redirectTo: '/renters'
  },
  {
    path: 'policy-application',
    loadChildren: () => import('./policy-application/policy-application.module').then(m => m.PolicyApplicationModule)
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then(m => m.AuthModule)
  },
  {
    path: 'user',
    loadChildren: () => import('./user/user.module').then(m => m.UserModule)
  },
  {
    path: 'brookfield',
    loadChildren: () => import('./brookfield/brookfield.module').then(m => m.BrookfieldModule),
    canActivate: [kioskGuard]
  },
  {
    path: 'assurant',
    canActivate:[assurantRedirectGuard],
    loadChildren:  () => import('./assurant/assurant.module').then(m => m.AssurantModule),
  },
  {
    path: 'renters',
    loadChildren:  () => import('./assurant/assurant.module').then(m => m.AssurantModule),
    canActivate: [amtrustRedirectGuard]
  },
  {
    /* Point Of Lease migration pages - Morgan Properties demo page */
    path: 'program',
    loadChildren: () => import('./point-of-lease/point-of-lease.module').then(m => m.PointOfLeaseModule),
    canActivate: [pointOfLeaseGuard]
  },
  {
    path: 'pma-tenant-onboarding',
    loadChildren: () => import('./pma/pma.module').then(m => m.PmaModule),
    canActivate: [uploadGuard]
  },
  {
    path: 'pma-tenant-onboarding/:token',
    loadChildren: () => import('./pma/pma.module').then(m => m.PmaModule),
    canActivate: [uploadGuard]
  },
  {
    path: 'upload-coverage-proof',
    component: PmaUploadCoverageProofComponent,
    // pmaModuleScopeGuard: this component is declared in the lazily-loaded
    // PmaModule but routed eagerly here — see the guard for why that breaks in dev.
    canActivate: [uploadGuard, pmaModuleScopeGuard]
  },
  {
    path: 'upload-coverage-proof/:token',
    component: PmaUploadCoverageProofComponent,
    canActivate: [uploadGuard, pmaModuleScopeGuard]
  },
  {
    path: 'faq',
    component: NewFaqPageComponent
  },
  {
    path: 'licenses',
    component: InsuranceProducerLicensureComponent
  },
  {
    path: 'giveback',
    component: GivebackPageComponent
  },
  {
    path: 'privacy-policy',
    component: PrivacyPolicyPageComponent
  },
  {
    path: 'Terms-of-Use',
    component: TermsOfUsePageComponent
  },
  {
    path: 'About-Us',
    component: AboutUsPageComponent,
    data: {pageTitle: 'about_us'}
  },
  {
    path: 'Contact-Us',
    component: ContactUsPageComponent,
    data: {pageTitle: 'contact_us'}
  },
  {
    path: 'process_payment',
    loadChildren: () => import('./process-payment/process-payment.module').then(m => m.ProcessPaymentModule)
  },
  {
    path: 'page/:title',
    component: StaticPageComponent
  },
  {
    path: 'not-supported',
    component: NotSupportedComponent
  },
  {
    path: 'dev-tools',
    loadChildren: () => import('./dev-tools/dev-tools.module').then(m => m.DevToolsModule)
  },
  {
    path: '**',
    redirectTo: ''
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, routerOptions)],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
